import { useTranslation } from "react-i18next";
import { STATUS_TONE_COLOR } from "../../lib/statusTone";

/**
 * EventsFeed — pattern_matcher events feed for the Today screen.
 *
 * Pure presentational atom. Caller hands us a list of events; each event
 * has a stable key, a short label, a one-line summary, a severity, and
 * a pre-formatted relative time string.
 *
 * Severity bands (colors come from the shared status table, statusTone.ts):
 *   OK / HIGH / GOOD → green (ok)
 *   WATCH / MED      → amber (watch)
 *   ALERT / LOW      → red (bad)
 *   INFO             → muted
 *
 * Empty-state: when events.length === 0 → soft "тихо за 7 дней" prompt.
 * The badge in the header always shows total count + N "new" if any.
 */

export type EventSeverity = "ok" | "good" | "watch" | "alert" | "info";

export interface PatternEvent {
  key: string;
  label: string;
  summary: string;
  severity: EventSeverity;
  /** Pre-formatted relative time, e.g. "1ч", "1д". */
  relativeTime?: string | null;
  /** Mark events newly arrived since last visit. */
  isNew?: boolean;
}

export interface EventsFeedProps {
  events: PatternEvent[];
  onSelect?: (event: PatternEvent) => void;
  className?: string;
}

/**
 * Цвета — только из STATUS_TONE_COLOR (statusTone.ts), свои var() здесь
 * не заводить. В тёмной теме значения побайтно прежние: --warn ≡
 * --warning-500, --bad ≡ --danger-500 (алиасы index.css:254,256). Сырой
 * var(--warning-500), стоявший тут раньше, в СВЕТЛОЙ теме обходил
 * затемнённый ради контраста алиас — теперь и WATCH идёт через таблицу.
 */
const SEVERITY_TONE: Record<EventSeverity, { dot: string; badge: string }> = {
  ok: { dot: STATUS_TONE_COLOR.ok, badge: "OK" },
  good: { dot: STATUS_TONE_COLOR.ok, badge: "GOOD" },
  watch: { dot: STATUS_TONE_COLOR.watch, badge: "WATCH" },
  alert: { dot: STATUS_TONE_COLOR.bad, badge: "ALERT" },
  info: { dot: STATUS_TONE_COLOR.muted, badge: "INFO" },
};

export function EventsFeed({ events, onSelect, className = "" }: EventsFeedProps) {
  const { t } = useTranslation("dashboard");
  const newCount = events.filter((e) => e.isNew).length;

  if (events.length === 0) {
    return (
      <div
        data-testid="events-feed-empty"
        className={`indigo-card p-4 ${className}`}
      >
        <div className="flex items-center gap-2 mb-2">
          <span className="w-[3px] h-3 rounded-sm bg-ok inline-block" />
          <span className="text-[9.5px] uppercase tracking-[0.22em] text-fg-muted font-bold">
            {t("events.section_label", { defaultValue: "События" })}
          </span>
        </div>
        <div className="text-xs text-fg-muted">
          {t("events.empty", { defaultValue: "За последние 7 дней — без событий." })}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="events-feed"
      className={`indigo-card p-4 ${className}`}
    >
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <span className="w-[3px] h-3 rounded-sm bg-ok inline-block" />
          <span className="text-[9.5px] uppercase tracking-[0.22em] text-fg-muted font-bold">
            {t("events.section_label", { defaultValue: "События" })}
          </span>
        </div>
        <span
          data-testid="events-feed-counter"
          className="text-[9px] uppercase tracking-[0.18em] text-accent font-bold tabular-nums"
        >
          {t("events.counter", {
            defaultValue: "{{n}} за 7 дн",
            n: events.length,
          })}
          {newCount > 0
            ? ` · ${t("events.new_suffix", {
                defaultValue: "{{n}} новых",
                n: newCount,
              })}`
            : ""}
        </span>
      </div>

      <div className="flex flex-col">
        {events.map((ev, i) => {
          const tone = SEVERITY_TONE[ev.severity] || SEVERITY_TONE.info;
          return (
            <div
              key={ev.key}
              data-testid={`events-feed-item-${ev.key}`}
              data-severity={ev.severity}
              data-is-new={ev.isNew ? "true" : "false"}
              onClick={onSelect ? () => onSelect(ev) : undefined}
              onKeyDown={
                onSelect
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelect(ev);
                      }
                    }
                  : undefined
              }
              role={onSelect ? "button" : undefined}
              tabIndex={onSelect ? 0 : undefined}
              className={`flex items-center gap-2.5 py-2.5 ${
                i > 0 ? "border-t border-bg-border/60" : ""
              } ${onSelect ? "cursor-pointer hover:bg-bg-raised/40 active:bg-bg-raised/70 transition" : ""}`}
            >
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: tone.dot }}
              />
              <span className="text-[12.5px] font-semibold text-fg-primary whitespace-nowrap">
                {ev.label}
              </span>
              <span className="text-[11.5px] text-fg-muted truncate flex-1 min-w-0">
                · {ev.summary}
              </span>
              <span
                className="text-[9px] uppercase tracking-[0.18em] font-bold flex-shrink-0"
                style={{ color: tone.dot }}
              >
                {/* Ярлык был захардкожен по-английски (OK/GOOD/WATCH/ALERT/
                    INFO) в компоненте, который в остальном локализован —
                    русскоязычный атлет видел английские метки. */}
                {t(`events_feed.severity.${ev.severity}`, { defaultValue: tone.badge })}
              </span>
              {ev.relativeTime && (
                <span className="text-[9px] uppercase tracking-[0.1em] text-fg-muted font-semibold flex-shrink-0 tabular-nums">
                  {ev.relativeTime}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
